"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Car, Bike, Sparkles, ChevronRight } from "lucide-react";
import dynamic from "next/dynamic";
import { vehicleImageUrl } from "@/lib/vehicle-api";
import { getApiUrl } from "@/lib/api-config";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);

const IKFooter = dynamic(
  () => import("@/components/IKFooter").then((m) => m.IKFooter),
  { ssr: true }
);

interface FleetVehicle {
  id: number;
  name: string;
  slug: string;
  body_type: string;
  image_url: string;
  vehicle_type: string;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

async function fetchFeaturedVehicles(
  vehicleType: "car" | "bike"
): Promise<FleetVehicle[]> {
  const res = await fetch(
    getApiUrl(`/api/v1/web/featured-vehicles?vehicle_type=${vehicleType}`),
    { headers: { Accept: "application/json" } }
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch featured ${vehicleType}s`);
  }
  const json = await res.json();
  if (!json.success || !Array.isArray(json.data)) return [];
  return json.data.map((v: any) => ({
    id: v.id,
    name: v.name,
    slug: v.slug || slugify(v.name),
    body_type: v.body_type,
    image_url: v.image_url,
    vehicle_type: v.vehicle_type === "bike" ? "bike" : "car",
  }));
}

function FleetCard({
  vehicle,
  index,
}: {
  vehicle: FleetVehicle;
  index: number;
}) {
  const [imgError, setImgError] = useState(false);
  const detailsHref = `/fleet/${vehicle.slug}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.05 }}
      className="group rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
    >
      <Link
        href={detailsHref}
        className="block relative h-48 overflow-hidden bg-gradient-to-br from-neutral-200 to-neutral-300 dark:from-neutral-700 dark:to-neutral-800"
      >
        {!imgError && vehicle.image_url ? (
          <img
            src={vehicleImageUrl(vehicle.image_url)}
            alt={vehicle.name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            {vehicle.vehicle_type === "bike" ? (
              <Bike className="w-16 h-16 text-neutral-400 dark:text-neutral-500" aria-hidden />
            ) : (
              <Car className="w-16 h-16 text-neutral-400 dark:text-neutral-500" aria-hidden />
            )}
          </div>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1 justify-between">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#FFB51D] mb-1">
            {vehicle.body_type || (vehicle.vehicle_type === "bike" ? "Two Wheeler" : "Four Wheeler")}
          </p>
          <h3 className="text-lg font-bold text-[#131222] dark:text-white group-hover:text-[#FFB51D] transition-colors line-clamp-1">
            <Link href={detailsHref} className="focus:outline-none focus:ring-2 focus:ring-[#FFB51D] rounded">
              {vehicle.name}
            </Link>
          </h3>
        </div>

        <div>
          <Link
            href={detailsHref}
            className="w-full inline-flex items-center justify-center rounded-xl border-2 border-[#FFB51D] text-[#FFB51D] hover:bg-[#FFB51D] hover:text-[#131222] py-2 px-4 text-sm font-bold transition-all duration-300"
          >
            View Details
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

export default function FleetPage() {
  const [cars, setCars] = useState<FleetVehicle[]>([]);
  const [bikes, setBikes] = useState<FleetVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [activeTab, setActiveTab] = useState<"cars" | "bikes">("cars");
  const [visibleCount, setVisibleCount] = useState(8);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchFeaturedVehicles("car"),
      fetchFeaturedVehicles("bike")
    ])
      .then(([carsData, bikesData]) => {
        if (!active) return;
        setCars(carsData);
        setBikes(bikesData);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error(err);
        setError("Failed to load fleet vehicles. Please try again later.");
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const activeVehicles = activeTab === "cars" ? cars : bikes;
  const visibleVehicles = activeVehicles.slice(0, visibleCount);

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 min-w-0">
        {/* Breadcrumbs */}
        <nav className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-6 font-medium" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0" aria-hidden />
          <span className="text-[#131222] dark:text-white font-semibold">
            Fleet
          </span>
        </nav>

        {/* Compact Page Header with Tabs Toggle */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 border-b border-neutral-200/40 dark:border-neutral-800/40 pb-6">
          <div className="max-w-xl">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#131222] dark:text-white tracking-tight flex items-center gap-2">
              Our Fleet
              <Sparkles className="w-6 h-6 text-[#FFB51D]" aria-hidden />
            </h1>
            <p className="mt-2 text-base text-neutral-600 dark:text-neutral-300">
              Choose from our wide range of Cars and Bikes for every journey.
            </p>
          </div>

          <div
            role="tablist"
            aria-label="Select Cars or Bikes"
            className="inline-flex rounded-xl border-2 border-neutral-200 dark:border-neutral-700 p-0.5 bg-neutral-50 dark:bg-neutral-800/20 self-start md:self-center shrink-0"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "cars"}
              onClick={() => {
                setActiveTab("cars");
                setVisibleCount(8);
              }}
              className={`px-5 py-2 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "cars"
                  ? "bg-[#FFB51D] text-[#131222] shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-[#131222] dark:hover:text-white"
              }`}
            >
              <Car className="w-4 h-4" />
              Cars
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "bikes"}
              onClick={() => {
                setActiveTab("bikes");
                setVisibleCount(8);
              }}
              className={`px-5 py-2 text-sm font-bold rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "bikes"
                  ? "bg-[#FFB51D] text-[#131222] shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-[#131222] dark:hover:text-white"
              }`}
            >
              <Bike className="w-4 h-4" />
              Bikes
            </button>
          </div>
        </div>

        {/* Loading and Error states */}
        {loading && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 animate-pulse">
                <div className="w-5 h-5 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
              </div>
              <div className="h-6 w-28 bg-neutral-200 dark:bg-neutral-800 rounded animate-pulse" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-md flex flex-col h-full animate-pulse"
                >
                  <div className="h-48 bg-neutral-200 dark:bg-neutral-800" />
                  <div className="p-5 flex flex-col flex-1 justify-between">
                    <div className="mb-6">
                      <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-1/4 mb-2" />
                      <div className="h-5 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4" />
                    </div>
                    <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded-xl w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="py-16 text-center rounded-2xl bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
            <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        )}

        {/* Fleet List */}
        {!loading && !error && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFB51D]/10 text-[#FFB51D]">
                {activeTab === "cars" ? <Car className="w-5 h-5" aria-hidden /> : <Bike className="w-5 h-5" aria-hidden />}
              </div>
              <h2 className="text-2xl font-bold text-[#131222] dark:text-white capitalize">
                Our {activeTab}
              </h2>
            </div>

            {visibleVehicles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {visibleVehicles.map((vehicle, i) => (
                    <FleetCard key={vehicle.id} vehicle={vehicle} index={i} />
                  ))}
                </div>

                {/* View More Button */}
                {visibleCount < activeVehicles.length && (
                  <div className="flex justify-center mt-12">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + 8)}
                      className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] px-8 py-3 text-sm font-bold text-[#131222] shadow-md hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#FFB51D] focus:ring-offset-2"
                    >
                      View More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-20 text-center rounded-2xl bg-neutral-100 dark:bg-neutral-800/50">
                <p className="text-neutral-600 dark:text-neutral-400">
                  No {activeTab} models are currently available in our fleet.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <IKFooter />
    </SimplePageBackground>
  );
}
